import React, {FC, useCallback} from 'react';

import {useAvailabilityStatus} from '../../../srcCommon/app/availabilityStatusHooks';
import {useAppDispatch, useAppSelector} from '../../helpers/reduxHelpers';
import {TeammateModel} from '../../models/api/teammateModel';
import {
  SettingsAdminHomeUrl,
  SettingsRootPagesEnum,
  SettingsUserHomeUrl,
} from '../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {getUserOwnedAnswersCount} from '../../store/data/answers/answersSelectors';
import {getCompanyPainPoints} from '../../store/data/company/companySelectors';
import {getUserOwnedInboxesCount} from '../../store/data/inboxes/inboxesSelectors';
import {
  updateDoNotDisturbAction,
  updateShouldMuteNotificationsAction,
} from '../../store/data/preferences/preferencesActions';
import {
  getDoNotDisturb,
  getShouldMuteNotifications,
  getTimeFormat,
} from '../../store/data/preferences/preferencesSelectors';
import {getUserOwnedSignaturesCount} from '../../store/data/signatures/signaturesSelectors';
import {saveUserAction, updateUserAvatar} from '../../store/data/teammates/teammatesActions';
import {
  getHomePendingTeammates,
  getIsUserGlobalAdmin,
  getUser,
} from '../../store/data/teammates/teammatesSelectors';
import {getFirstTeamUserIsAdminOf} from '../../store/data/teams/teamsSelectors';
import {getFlatVacationRespondersCount} from '../../store/data/vacationResponders/vacationRespondersSelectors';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {navigateToLinkAction} from '../../store/stateless/navigationActions';
import {makeSettingsUrlFromPathId} from '../menu/settingsHelpers';
import {SettingsHome} from './settingsHome';
import {SettingsHomePendingTeammateProps} from './settingsHomePendingTeammates';
import {useHomeShortcutClick} from './settingsHomeShortcutHook';
import {ProfileTogglesEnum, ProfileTogglesState} from './settingsHomeTypes';

/*
 * Props.
 */

interface SettingsUserHomeContainerProps {
  panelId: SettingsPanelId;
  url: SettingsUserHomeUrl | SettingsAdminHomeUrl;
}

/*
 * Component.
 */

export const SettingsHomeContainer: FC<SettingsUserHomeContainerProps> = (props) => {
  const dispatch = useAppDispatch();
  const painPoints = useAppSelector(getCompanyPainPoints);
  const onArticleClick = (title: string) => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_HOMEPAGE_CLICK, {
      click_type: 'guide',
      click_object: title,
      home_type: 'user',
    });
  };
  const {onShortcutClick, maybeRenderDialogs} = useHomeShortcutClick(props.url, props.panelId);
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const doNotDisturb = useAppSelector(getDoNotDisturb);
  const shouldMuteNotifications = useAppSelector(getShouldMuteNotifications);
  const user = useAppSelector(getUser);
  const timeFormat = useAppSelector(getTimeFormat);
  const availabilityStatus = useAvailabilityStatus();
  const homePendingTeammates = useAppSelector(getHomePendingTeammates);
  const firstTeam = useAppSelector(getFirstTeamUserIsAdminOf);
  const makeOnTeammateClick = (teammate: TeammateModel) => () => {
    if (!firstTeam) {
      return;
    }

    if (isGlobalAdmin) {
      const newUrl = makeSettingsUrlFromPathId('settings.teammates.edit.overview', {
        teammate: teammate.alias,
        namespace: 'global',
      });

      dispatch(navigateToLinkAction(newUrl));
    }
  };
  const teammateProps: ReadonlyArray<SettingsHomePendingTeammateProps> = homePendingTeammates.map((t) => ({
    teammate: t,
    onClick: makeOnTeammateClick(t),
  }));
  const userOwnedInboxesCount = useAppSelector(getUserOwnedInboxesCount);
  const userOwnedAnswersCount = useAppSelector(getUserOwnedAnswersCount);
  const flatVacationRespondersCount = useAppSelector(getFlatVacationRespondersCount);
  const userOwnedSignaturesCount = useAppSelector(getUserOwnedSignaturesCount);

  const onProfileToggleChange = useCallback(
    (profileToggleState: ProfileTogglesState) => {
      dispatch(
        updateDoNotDisturbAction({
          ...doNotDisturb,
          isEnabled: profileToggleState[ProfileTogglesEnum.DO_NOT_DISTURB],
        }),
      );
      dispatch(updateShouldMuteNotificationsAction(profileToggleState[ProfileTogglesEnum.MUTE]));
      dispatch(saveUserAction({isAvailable: !profileToggleState[ProfileTogglesEnum.OUT_OF_OFFICE]}));
    },
    [dispatch, doNotDisturb],
  );
  const onAvatarChange = useCallback(
    (file: File) => {
      dispatch(updateUserAvatar(file));
    },
    [dispatch],
  );

  return (
    <>
      <SettingsHome
        painPoints={painPoints}
        teammateProps={teammateProps}
        onArticleClick={onArticleClick}
        onShortcutLinkClick={onShortcutClick}
        isGlobalAdmin={isGlobalAdmin}
        onProfileToggleChange={onProfileToggleChange}
        onAvatarChange={onAvatarChange}
        doNotDisturb={doNotDisturb}
        shouldMuteNotifications={shouldMuteNotifications}
        shouldRenderAdmin={props.url.rootPage === SettingsRootPagesEnum.ADMIN_HOME}
        user={user}
        userTimeFormat={timeFormat}
        userAvailabilityStatus={availabilityStatus}
        personalInboxesCount={userOwnedInboxesCount}
        personalAnswersCount={userOwnedAnswersCount}
        personalRespondersCount={flatVacationRespondersCount}
        personalSignaturesCount={userOwnedSignaturesCount}
        panelId={props.panelId}
      />
      {maybeRenderDialogs()}
    </>
  );
};
